import React from 'react';
import { Box } from '@mui/material';
import SEOHead from '../components/SEO/SEOHead';
import SchemaMarkup from '../components/SEO/SchemaMarkup';
import { styled } from '@mui/material/styles';
import ResponsiveContainer from '../components/layout/ResponsiveContainer';
import PageBackground from '../components/layout/PageBackground';
import HeroSection from '../components/HeroSection';
import TrustSection from '../components/TrustSection';
import DesignerBioSection from '../components/DesignerBioSection';
import ProcessSection from '../components/ProcessSection';
import SailTypesSection from '../components/SailTypesSection';
import TestimonialCarousel from '../components/TestimonialCarousel';
import TransparentPricingSection from '../components/TransparentPricingSection';
import BoatsSupportedSection from '../components/BoatsSupportedSection';
import ContactQuestionsSection from '../components/ContactQuestionsSection';
import ScrollToTop from '../components/shared/ScrollToTop';
import HomePageSearch from '../components/HomePageSearch';

const SECTION_SPACING = {
    AFTER_HERO: '48px',
    BETWEEN_SECTIONS: '80px',
    MOBILE: {
        AFTER_HERO: '32px',
        BETWEEN_SECTIONS: '48px'
    }
} as const;

const PageWrapper = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
    width: '100%',
    overflowX: 'hidden', // Prevent horizontal scroll
    [theme.breakpoints.down('sm')]: {
        maxWidth: '100vw', // Ensure it doesn't exceed viewport width
    },
}));

const HomePage: React.FC = () => {
    return (
        <PageWrapper>
            <SEOHead page="home" />
            <SchemaMarkup type="organization" />
            <SchemaMarkup type="website" />
            <ScrollToTop />

            <PageBackground>
                {/* Header compensation */}
                <Box sx={{ height: { xs: '0px', sm: '30px' } }} />

                {/* Hero Section */}
                <ResponsiveContainer
                    fullWidth
                    padding="0"
                    mobilePadding="0"
                    sx={{
                        mb: {
                            xs: SECTION_SPACING.MOBILE.AFTER_HERO,
                            md: SECTION_SPACING.AFTER_HERO
                        }
                    }}
                >
                    <HeroSection />
                </ResponsiveContainer>
                {/* Mobile CTA Bar - only visible on mobile (desktop CTA is in HeroSection) */}
                <ResponsiveContainer
                    maxWidth="lg"
                    padding="0 16px"
                    mobilePadding="0 16px"
                    sx={{ display: { xs: 'block', md: 'none' } }}
                >
                    <HomePageSearch />
                </ResponsiveContainer>

                <Box component="main" sx={{
                    '& > *:not(:last-child)': {
                        marginBottom: {
                            xs: SECTION_SPACING.MOBILE.BETWEEN_SECTIONS,
                            md: SECTION_SPACING.BETWEEN_SECTIONS
                        }
                    }
                }}>
                    {/* Trust Section (NEW - replaces TopReasonsSection) */}
                    <ResponsiveContainer
                        maxWidth="lg"
                        padding="24px"
                        mobilePadding="16px"
                    >
                        <TrustSection />
                    </ResponsiveContainer>

                    {/* Designer Bio Section */}
                    <ResponsiveContainer
                        maxWidth="lg"
                        padding="24px"
                        mobilePadding="16px"
                    >
                        <DesignerBioSection />
                    </ResponsiveContainer>

                    {/* Process Section (NEW) */}
                    <ResponsiveContainer
                        maxWidth="xl"
                        padding="24px"
                        mobilePadding="16px"
                    >
                        <ProcessSection />
                    </ResponsiveContainer>

                    {/* Testimonials Section (moved up from bottom) */}
                    <ResponsiveContainer
                        maxWidth="lg"
                        padding="24px"
                        mobilePadding="16px"
                    >
                        <TestimonialCarousel />
                    </ResponsiveContainer>

                    {/* Transparent Pricing Section */}
                    <ResponsiveContainer
                        maxWidth="lg"
                        padding="24px"
                        mobilePadding="16px"
                    >
                        <TransparentPricingSection />
                    </ResponsiveContainer>

                    {/* Boats Supported Section */}
                    <ResponsiveContainer
                        maxWidth="lg"
                        padding="24px"
                        mobilePadding="16px"
                    >
                        <BoatsSupportedSection />
                    </ResponsiveContainer>

                    {/* Contact/Questions Section (NEW) */}
                    <ResponsiveContainer
                        maxWidth="lg"
                        padding="24px"
                        mobilePadding="16px"
                    >
                        <ContactQuestionsSection />
                    </ResponsiveContainer>
                </Box>
            </PageBackground>
        </PageWrapper>
    );
};

export default HomePage;
